<script>
	import { mapGetters, mapState } from "vuex";
	import i18n from "@/i18n";
	import BaseSelect from "../components/UI/BaseSelect";
	import BaseStylableTextarea from "../components/UI/BaseStylableTextarea";
	import DisabledStylableTextarea from "../components/UI/DisabledStylableTextarea";
	import LogoUpload from "../components/Home/LogoUpload";
	import RecurringCard from "../components/Recurring/RecurringCard";
	import BillingTable from "../components/Billing/Table";
	import VatTable from "../components/Vat/Table";
	import DownpaymentTable from "../components/Downpayment/Table";
	import DownpaymentWithoutInvoice from "../components/Downpayment/DownpaymentWithoutInvoice";
	import ModalBankAccountCreate from "../components/Modals/ModalBankAccountCreate";
	import ModalBankAccountDelete from "../components/Modals/ModalBankAccountDelete";
	import ModalBillingSave from "../components/Modals/ModalBillingSave";
	import ModalRegister from "../components/Modals/ModalRegister";
	import Menu from "../components/Menu/Menu";
	import ClientPicker from "../components/Home/ClientPicker";
	import PopoverBillingFormatRadioList from "../components/Popovers/PopoverBillingFormatRadioList";
	import PopoverOrganizationProfile from "../components/Popovers/PopoverOrganizationProfile";
	import PopoverPenal from "../components/Popovers/PopoverPenal";
	import ModalDocumentStyle from "../components/Onboarding/ModalDocumentStyle";
	import PopoverLogo from "../components/Onboarding/PopoverLogo.vue";
	import PopoverNumbering from "../components/Onboarding/PopoverNumbering.vue";
	import PopoverCompany from "../components/Onboarding/PopoverCompany.vue";
	import PopoverPayment from "../components/Onboarding/PopoverPayment.vue";
	import PopoverBankDetails from "../components/Onboarding/PopoverBankDetails.vue";
	import PopoverEndOnboarding from "../components/Onboarding/PopoverEndOnboarding.vue";
	import ModalCreateOrganization from "@/components/Modals/ModalCreateOrganization.vue";
	import ModalEditAddress from "@/components/Modals/ModalEditAddress.vue";
	import ModalCreatePerson from "@/components/Modals/ModalCreatePerson.vue";
	import ModalEditEstablishmentContact from "@/components/Modals/ModalEditEstablishmentContact.vue";
	import BaseMultiselect from "@/components/UI/BaseMultiselect.vue";

	import {
		addTimeToDate,
		reformatIsoStringToDate,
		interceptEvent,
		getSuggestedVatTypeFromSections
	} from "@/mixins/methods";

	export default {
		components: {
			BaseMultiselect,
      		ModalEditEstablishmentContact,
			ModalCreatePerson,
			ModalEditAddress,
			ModalCreateOrganization,
			RecurringCard,
			BaseSelect,
			BillingTable,
			VatTable,
			DownpaymentTable,
			BaseStylableTextarea,
			DisabledStylableTextarea,
			DownpaymentWithoutInvoice,
			ModalBankAccountCreate,
			ModalBankAccountDelete,
			ModalBillingSave,
			ModalRegister,
			Menu,
			ClientPicker,
			LogoUpload,
			PopoverBillingFormatRadioList,
			PopoverOrganizationProfile,
			PopoverPenal,
			ModalDocumentStyle,
			PopoverLogo,
			PopoverNumbering,
			PopoverCompany,
			PopoverPayment,
			PopoverBankDetails,
			PopoverEndOnboarding
		},
		data() {
			return {
				minWrittingDate: null,
				minCommercialValidityDeadline: new Date(),
				tooltipBillingNumberMsg: "",
				nextDocument: null,
				showBankModalCreate: false,
				showBankModalDelete: false,
				showFooterPopover: false,
				showPenalPopover: false,
				regParams: new RegExp(/%s/, 'i'),
				language: "fr",
				typeEditedManually: false
			};
		},
		computed: {
			...mapGetters(["organization", "config", "totals", "currency", "paymentDelay", "paymentMethods", "is_vat_subject"]),
			...mapState(["home", "vat", "page", "settings", "onboarding", "app"]),
			...mapState({
				loading: state => state.home.loading,
				lastDocument: state => state.home.lastDocument,
				loadedDocument: state => state.home.loadedDocument,
				bankdetails: state => state.home.bankdetails,
				appId: state => state.app.id,
				documentId: state => state.app.documentId,
				isRecurringInvoice: state => state.recurring.isRecurringInvoice,
				paymentTextDays: state => state.home.paymentTextDays,
				vatName: state => state.vat.vatName,
				third_account_enabled: state => state.settings.config.accounting.third_account_enabled,
				currencies: state => state.settings.currencies,
				shlould_notify_payment_methods_period: state => state.home.shlould_notify_payment_methods_period,
				prefered_vat_value: state => state.app.config?.accounting?.vat?.preferedValue?? null,
				isEditable: state => state.home.isEditable,
				showCreateOrganizationModal: state => state.home.showCreateOrganizationModal,
				showCreatePersonModal: state => state.home.showCreatePersonModal,
				showEditAddressModal: state => state.home.showEditAddressModal,
				showEditEstablishmentContactModal: state => state.home.showEditEstablishmentContactModal,
        		showModalDocumentStyleCloseButton: state => state.onboarding.showModalDocumentStyleCloseButton,
				pageType: state => state.page.type
			}),
			factureXLogo() {
				// return this.app.app.url.replace(("/"+this.app.app.identifiant), "") + "front/assets/images/facturex/logo_facture-x-large.png";
				return "https://sinao.app/front/assets/images/facturex/logo_facture-x-large.png";
			},
			isFrenchSalesDocument() {
				return (this.page.type === "invoices" || this.page.type === "quotes") && this.organization.country_iso2 === 'FR';
			},
			documentType() {
				return this.page.type === "invoices" ? (this.loadedDocument && this.loadedDocument.credit_note_id  ? "Avoir" : "Facture") : "Devis";
			},
			documentTitle() {
				if (this.loadedDocument?.imported_at) return "Duplicata";
				return this.page.type === "invoices" ? (this.loadedDocument && this.loadedDocument.credit_note_id  ? "Avoir" : "Facture") : "Devis";
			},
			organizationCapitalCurrency() {
				const currencyFounded = this.currencies.find(currency => currency.iso === this.config?.currency);
				return currencyFounded?.symbol || this.currency;
			},
			vatExoneration() {
				return this.$store.getters.vatExoneration;
			},
			legalMentionText() {
				const nationalIdMention =  this.organization.siret && this.organization.siret.length >= 15 ? "SIRET" : "SIREN";

				const firstLine = [
					// SINAO au capital de 10 000,00€
					this.organization.legal_form ? `<b>${this.organization.legal_form}</b>` : '',
					`<b>${this.organization.billing_name}</b>`,
					this.organization.capital ?
						i18n?.t("au capital de {amount} {currency}", {
							amount: this.convertNumberToString(this.organization.capital),
							currency: this.organizationCapitalCurrency
						}) : ''
				];
				const secondLine = [
					// SIRET 811340728 - NAF
					this.organization.siret ? `${nationalIdMention} ${this.organization.siret}` : '',
					this.organization.code_naf ?
						i18n?.t("Code NAF {code}", { code: this.organization.code_naf }) : ''
				];
				const thirdLine = [
					//N°TVA FR 32 811 340 728 - RCS xxx - RM xxx
					this.organization.vat_id ? `N°${i18n.t(this.vatName)} ${this.organization.vat_id}` : '',
					this.organization.rcs ? `RCS ${this.organization.rcs} ${this.organization.founding_location ?? ''}` : '',
					this.organization.trade_directory_registration ? `RM ${this.organization.trade_directory_registration}  ${this.organization.founding_location ?? ''}` : '',
					this.organization.sap_number_registration ? `${this.organization.sap_number_registration}` : ''
				];
				let fourthLine = [];
				if(this.organization.headquarters)
					fourthLine = [
						// 29 Chemin de l'Église - 38100 Grenoble - France - Tel : 0627807198 - contact@sinao.fr
						this.organization.headquarters.place.formatted_address,
						(this.organization.headquarters.phones && this.organization.headquarters.phones[0]) ? `Tél. ${this.organization.headquarters.phones[0]}` : '',
						(this.organization.headquarters.emails && this.organization.headquarters.emails[0]) ? this.organization.headquarters.emails[0] : '',
					];

				return firstLine.filter(n => n).join(' ') + "<br />" +
					   secondLine.filter(n => n).join(' - ') + "<br />" +
					   thirdLine.filter(n => n).join(' - ') + "<br />" +
					   fourthLine.filter(n => n).join(' - ')
			},
			showBillingPopover: {
				get() {
					return this.$store.state.home.showBillingPopover;
				},
				set(value) {
					this.$store.commit("showBillingPopover", value);
				}
			},
			showDetails: {
				get() {
					return this.$store.state.home.showDetails;
				},
				set(value) {
					this.$store.commit("showDetails", value);
				}
			},
			showDownpaymentTable: {
				get() {
					return this.$store.state.home.showDownpaymentTable;
				},
				set(value) {
					this.$store.commit("showDownpaymentTable", value);
				}
			},
			showDownpaymentWithoutInvoice: {
				get() {
					return this.$store.state.home.showDownpaymentWithoutInvoice;
				},
				set(value) {
					this.$store.commit("showDownpaymentWithoutInvoice", value);
				}
			},
			writtingDate: {
				get() {
					return this.$store.state.home.writtingDate;
				},
				set(value) {
					this.$store.commit("writtingDate", value);
				}
			},
			deliveredAt: {
				get() {
					return this.$store.state.home.deliveredAt;
				},
				set(value) {
					this.$store.commit("deliveredAt", value);
				}
			},
			commercialvalidityDeadline: {
				get() {
					return this.$store.state.home.commercialvalidityDeadline;
				},
				set(value) {
					this.$store.commit("commercialvalidityDeadline", value);
				}
			},
			billingNumber: {
				get() {
					return this.$store.state.home.billingNumber;
				},
				set(value) {
					this.$store.commit("billingNumber", value);
				}
			},
			detailsTwo: {
				get() {
					return this.$store.state.home.detailsTwo;
				},
				set(value) {
					this.$store.commit("detailsTwo", value);
				}
			},
			details: {
				get() {
					return this.$store.state.home.details;
				},
				set(value) {
					this.$store.commit("details", value);
				}
			},
			commitment_number: {
				get() {
				return this.$store.state.home.commitment_number;
				},
				set(value) {
				this.$store.commit("commitment_number", value);
				}
			},
			commitment_number_valid() {
				if (!this.commitment_number?.length) return null
				const reg = /[&<>"']/g
				return reg.test(this.commitment_number) ? false : null;
			},
			title: {
				get() {
					return this.$store.state.home.title;
				},
				set(value) {
					this.$store.commit("title", value);
				}
			},
			selectedRIB: {
				get() {
					return this.$store.state.home.selectedRIB;
				},
				set(value) {
					this.$store.commit("selectedRIB", value);
				}
			},
			vatType: {
				get() {
					return this.$store.state.home.vatType ?? 'service';
				},
				set(value) {
					this.$store.commit("vatType", value);
				}
			},
			vat_regime: {
				get() {
					return this.$store.state.home.vat_regime;
				},
				set(value) {
					this.$store.commit("vat_regime", value);
				}
			},
			selectedTypeOption: {
				get() {
					return this.typeOptions.find(option => option.value === this.vatType) ?? null;
				},
				set(option) {
					this.vatType = option?.value ?? null;
				}
			},
			selectedVatRegimeOption: {
				get() {
					return this.vatRegimeOptions.find(option => option.value === this.vat_regime) ?? null;
				},
				set(option) {
					this.vat_regime = option?.value ?? null;
				}
			},
			typeOptions() {
				return [
					// { value: null, text: this.$t("Sélectionner un type d’opération") },
					{ value: "product", text: this.$t("Livraison de biens") },
					{ value: "service", text: this.$t("Préstations de services") },
					{ value: "mixed", text: this.$t("Mixte") }
				];
			},
			vatRegimeOptions() {
				return [
					// { value: null, text: this.$t("Sélectionner un mode de TVA") },
					{ value: "receipt", text: this.$t("TVA sur les encaissements") },
					{ value: "debit", text: this.$t("TVA sur les débits") }
				];
			},
			vatExonerationReason: {
				get() {
					return this.$store.state.home.vatExonerationReason ?? this.vatExoneration[0].reason;
				},
				set(value) {
					this.$store.commit("vatExonerationReason", value);
				}
			},
			vatExonerationArticle: {
				get() {
					return this.$store.state.home.vatExonerationArticle ?? this.vatExoneration[0].article;
				},
				set(value) {
					this.$store.commit("vatExonerationArticle", value);
				}
			},
			mention: {
				get() {
					return this.$store.state.home.mention;
				},
				set(value) {
					this.$store.commit("mention", value); // will be in HTML
				}
			},
			mentionAsDefault: {
				get() {
					return this.$store.state.home.mentionAsDefault;
				},
				set(value) {
					this.$store.commit("mentionAsDefault", value);
				}
			},
			penalityAmount: {
				get() {
					return this.$store.state.home.penalityAmount;
				},
				set(value) {
					this.$store.commit("penalityAmount", value);
				}
			},
			penalityPercentage: {
				get() {
					return this.$store.state.home.penalityPercentage;
				},
				set(value) {
					this.$store.commit("penalityPercentage", value);
				}
			},
			recurring_frequencyDuration() {
				return this.$store.state.recurring.frequencyDuration;
			},
			recurring_periods() {
				const _period = this.$store.state.recurring.periods[this.recurring_frequencyDuration];
				if (_period === undefined) return {} // on mounted when nothing is settled

				const translated_response = {};
				Object.keys(_period).forEach(key => {
				  translated_response[key] = Array.isArray(_period[key])
					  ? _period[key].reduce(
						  (accumulator, currentValue) =>
							  accumulator
							  + " "
							  + (currentValue.text !== null ? i18n.t(currentValue.text, {data: currentValue.data}) : currentValue.data),
						  "",
					  )
					  : "";
				});
				return Object.assign({
					  none_: i18n.t("Ne pas préciser de période") },
					  translated_response
				);
			},
			recurring_period: {
				get() {
					return this.$store.state.recurring.period;
				},
				set(value) {
          			// value is the index of the object of this.recurring_periods
					this.$store.commit("period", value);
					const period_text_to_display = value === "none_" ? "" : value;
					this.updateDetailsForPeriod(period_text_to_display);
				}
			},
			showInvoicePenalities() {
				return this.$store.state.home.penalityTextTemplate.length > 2 || this.$store.state.home.indemnityTextTemplate.length > 2;
			},
			invoicesPenalities() {
				if (this.$store.state.home.penalityTextTemplate.length> 2 ) {
					if (this.regParams.test(this.$store.state.home.penalityTextTemplate)) {
						return this.$store.state.home.penalityTextTemplate.split("%s");
					} else {
						return [this.$store.state.home.penalityTextTemplate, ""]
					}
				}
				return ["", ""]
			},
			invoicesIndemnities() {
				if (this.$store.state.home.indemnityTextTemplate.length > 2) {
					if (this.regParams.test(this.$store.state.home.indemnityTextTemplate)) {
						return this.$store.state.home.indemnityTextTemplate.split("%s");
					} else {
						return [this.$store.state.home.indemnityTextTemplate, ""]
					}
				}
				return ["", ""]
			}

		},
		watch: {
			lastDocument: {
				handler() {
					if (this.lastDocument) {
						this.initialiseLastDocument();
					}
				},
			},
			loading: {
				immediate: true,
				async handler() {
					if (!this.isRecurringInvoice) {
						// No document is provided
						if (!this.documentId) {
							if (this.appId) {
								this.nextDocument = await this.$store.dispatch(
									"nextDocument",
									addTimeToDate(new Date())
								);
							}

							this.billingNumber = this.appId ? this.nextDocument?.number : "FAC00001";

							this.writtingDate = new Date().toISOString().replace(".000Z", "Z");

							const datePlusDays = new Date();
							datePlusDays.setDate(datePlusDays.getDate() + 30);

							this.commercialvalidityDeadline = datePlusDays.toISOString().replace(".000Z", "Z");

							// It's a new document and the first one
							this.tooltipBillingNumberMsg = i18n.t("Cliquez ici pour configurer la numérotation de vos documents.");

							// Load data from the lastDocument
							if (this.lastDocument) {
								this.initialiseLastDocument();
							} else {
								this.$store.commit("vatType", "service");
							}
						}
						// A document is provided
						else {
							// The provided document is loaded
							if (this.loadedDocument) {
								// It's a draft document
								if (this.loadedDocument?.status === "draft") {
									this.nextDocument = await this.$store.dispatch(
										"nextDocument",
										new Date(this.loadedDocument?.written_at).toISOString().replace(".000Z", "Z")
									);

									this.billingNumber = this.nextDocument?.number;

									this.tooltipBillingNumberMsg = i18n.t("Le numéro sera attribué après confirmation définitive du document.");
								}
								// It's a final document
								else {
									this.tooltipBillingNumberMsg = i18n.t("Le document a été confirmé, il est impossible de modifier son numéro.");
								}
							}
						}
					}
				}
			},
			recurring_frequencyDuration(value) {
				if (value === "day") {
					//this.recurring_period= "none_";
					this.updateDetailsForPeriod("");
				} else {
					const period_text_to_display = this.recurring_period === "none_" ? "" : this.recurring_period;
					this.updateDetailsForPeriod(period_text_to_display);
				}
			},

			paymentDelay(value) {
				if (this.shlould_notify_payment_methods_period) {
					const plural =  value > 1 ? "s" : "";
					const messageDelay = value === 0
						? i18n.t("Paiement au comptant")
						: i18n.t("Paiement sous {days} jour{plural}", { days: value, plural: plural });
					this.$bvToast.toast(messageDelay, {
						title: i18n.t("Délai de règlement"),
						variant: "info",
						solid: true,
						toaster: "b-toaster-bottom-left"
					});
				}
			},

			paymentMethods(value, old_value) {
				if ((old_value && old_value.length === 0) || value === old_value) return

				if (this.shlould_notify_payment_methods_period) {
					const messageMethods = i18n.t("Paiement via {paymentMethod}", { paymentMethod: value.replace(/,(\S)/g, ", $1") });
					this.$bvToast.toast(messageMethods, {
						title: i18n.t("Méthodes de règlement"),
						variant: "success",
						solid: true,
						toaster: "b-toaster-bottom-left"
					});
				}
			},
		},
		async created() {
			if (process.env.NODE_ENV === "production") {
				// Prompt the user to validate the action of leaving the page
				window.onbeforeunload = function() {
					return i18n.t("Êtes-vous sûrs de vouloir fermer la page ?");
				};
			}
			this.language = i18n.locale;

			// Listen for external events
			window.addEventListener("message", interceptEvent, false);
    	},
		methods: {
			isValidVatType(value) {
				return ["product", "service", "mixed"].includes(value);
			},
			onTypeSelect(option) {
				this.typeEditedManually = true;
				this.vatType = option?.value ?? null;
			},
			onTypeRemove() {
				this.typeEditedManually = true;
				this.vatType = null;
			},
			selectVatExoneration() {
				const reason = String(this.vatExonerationReason);
				const exemption = this.vatExoneration.find(item => String(item.reason) === reason);
				const article = exemption ? exemption.article : "";

				this.vatExonerationReason = reason;
				this.vatExonerationArticle = i18n.t(article);
			},
			onChangePageType(value) {
				this.$store.commit("pageType", value);
			},
			setTable(jsonBill) {
				this.$nextTick(() => this.$refs.billingTable.setTable(jsonBill));
			},
			billingTableEdition(status) {
				this.$nextTick(() => this.$refs?.billingTable?.billingTableEdition(status));
			},
			// calculate the inner text width of an input for inline inputs
			calculateTextWidth(text, font = "13px Roboto") {
				// if given, use cached canvas for better performance
				// else, create new canvas
				const canvas =
					this.calculateTextWidth.canvas ||
					(this.calculateTextWidth.canvas = document.createElement("canvas"));
				const context = canvas.getContext("2d");

				context.font = font;
				const metrics = context.measureText(text);

				return metrics.width;
			},
			saveDocument() {
				if (this.appId === 0) {
					this.home.showRegisterModal = true;
				} else {
					this.$store.dispatch("saveDocument");
				}
			},
			closeAllPopover() {
				this.showFooterPopover = false
				this.showPenalPopover = false
			},
			updateDetailsForPeriod(period_text) {
				// Exemple : {test}
				const pattern = /\s*\{[a-z-_]{2,}\}/gi;
				const isEmptyBlock = block => !block.textContent.replaceAll(/\u00a0/g, " ").trim();

				const container = document.createElement("div");
				container.innerHTML = this.details || "";

				const getBlocks = () => Array.from(container.querySelectorAll("p, li"));

				// Supprime les patterns existants
				getBlocks().forEach(block => {
					block.innerHTML = block.innerHTML.replaceAll(pattern, "");
				});

				// Supprime les blocs vides en fin
				let blocks = getBlocks();
				while (blocks.length && isEmptyBlock(blocks.at(-1))) {
					blocks.at(-1).remove();
					blocks = getBlocks();
				}

				// Insère le nouveau pattern
				if (period_text) {
					const target = blocks.at(-1) ?? container.appendChild(document.createElement("p"));
					target.textContent = `${target.textContent.trimEnd()} {${period_text}}`;
				}

				this.details = container.innerHTML;
			},
			initialiseLastDocument() {
				// do nothing if LoadedDocument
				if (this.loadedDocument !== null)
					return false;

				// It's a new document but not the first one
				this.tooltipBillingNumberMsg = i18n.t("Le numéro sera attribué après confirmation définitive du document.");

				this.minWrittingDate = reformatIsoStringToDate(this.lastDocument?.written_at);

				if (this.lastDocument?.columns) {
					Object.entries(this.lastDocument?.columns).forEach(([key, value]) => {
						if (value !== false) {
							this.$store.commit("renameHeaderByKey", { key, value });
							this.$store.commit("enableHeaderByKey", key);
						} else if (value === false) {
							this.$store.commit("disableHeaderByKey", key);
						}
					});
				}

				if (this.lastDocument?.bank_details_id) {
					this.$store.commit("selectedRIB", {
						id: this.lastDocument?.bank_details_id
					});
				}
				const lastDocumentVatType = this.isValidVatType(this.lastDocument?.billing_type)
					? this.lastDocument.billing_type
					: "service";
				this.$store.commit("vatType", lastDocumentVatType);
				this.typeEditedManually = this.isValidVatType(this.lastDocument?.billing_type);

				this.$store.commit(
					"paymentDelayConfig",
					this.lastDocument?.payment_period ? this.lastDocument?.payment_period : 0
				);

				if (this.prefered_vat_value === null && this.lastDocument?.totals?.taxes?.vat) {
					this.$store.commit(
						"selectedVatValue",
						Object.keys(this.lastDocument?.totals?.taxes?.vat)[0] / 100 ?? 20
					);
				} else if (this.prefered_vat_value !== null) {
					this.$store.commit("selectedVatValue", this.prefered_vat_value);
				}

				if (this.lastDocument?.vat_exemption?.exempted === true) {
					this.$store.commit("selectedVatValue", 0);
					const _last_reason = this.lastDocument?.vat_exemption?.reason;
					const _exemption_reason =
						_last_reason
						&& this.config?.accounting?.vat_exemption[_last_reason]
						&& !this.config?.accounting?.vat_exemption[_last_reason]?.deleted_at
							? _last_reason
							: "null";

					const _article = Object.hasOwn(this.config?.accounting?.vat_exemption ?? {}, _exemption_reason)
						? this.config?.accounting?.vat_exemption?.[_exemption_reason]?.text
						: this.lastDocument?.vat_exemption?.article;

					this.$store.commit("vatExonerationReason", _exemption_reason);
					this.$store.commit("vatExonerationArticle", i18n.t(_article));
				}
			},
			closeModal(commiter) {
				this.$store.commit(commiter, false)
			}
		}
	};
</script>

<template>
<div
		id="end-onboarding"
		@click="closeAllPopover"
	>
		<RecurringCard
			v-if="isRecurringInvoice && !loading"
			@event-loadModel="setTable"
			@event-billingTableEdition="billingTableEdition"
		/>

		<b-card class="A4" id="A4">
			<b-overlay v-if="loading" :show="loading" style="margin-top: 25%; background-color: transparent">
				<template #overlay>
					<div class="text-center">
						<b-icon icon="stopwatch" font-scale="3" animation="cylon" />
						<p id="cancel-label">
							{{ $t("Chargement de votre éditeur...") }}
						</p>
					</div>
				</template>
			</b-overlay>

			<b-col v-else>
				<!-- LOGO & TITLE -->
				<b-row v-if="!isRecurringInvoice" style="margin-top: 30px">
					<!-- LOGO -->
					<b-col cols="6">
						<LogoUpload />
					</b-col>

					<!-- TITLE -->
					<b-col cols="6" style="text-align: right">
						<p class="sinao-title">
							{{ $t(documentTitle) }}
						</p>
					</b-col>
				</b-row>

				<!-- INPUT & CLIENT SELECTION -->
				<b-row class="editHeader">
					<!-- INPUT -->
					<b-col cols="6" class="mb-3 mt-4 pr-4">
						<Fragment v-if="!isRecurringInvoice">
							<b-row>
								<b-col cols="5" v-if="isFrenchSalesDocument" v-b-tooltip.hover.html.right="$t('tooltip_document_type')">
									<label class="sinao-label" for="vat-basis-select">
										{{ $t("Type d’opération") }}
									</label>
								</b-col>
								<b-col cols="7" class="pr-4" v-if="isFrenchSalesDocument">
									<div class="vat-basis-wrapper">
										<BaseMultiselect
											id="vat-basis-select"
											data-name="document-type-select"
											:value="$t(selectedTypeOption) ?? null"
											:options="typeOptions"
											:show-labels="false"
											:allow-empty="true"
											:searchable="false"
											:placeholder="$t('Catégorie')"
											label="text"
											track-by="value"
											custom-class="floating-model-menu"
											@input="selectedTypeOption = $event"
											@select="onTypeSelect"
											@remove="onTypeRemove"
										/>
									</div>
								</b-col>
								<b-col cols="5" v-if="isFrenchSalesDocument && is_vat_subject " v-b-tooltip.hover.html.right="$t('tooltip_vat_regime')">
									<label class="sinao-label" for="vat-regime-select">
										{{ $t("TVA applicable") }}
									</label>
								</b-col>
								<b-col cols="7" v-if="isFrenchSalesDocument && is_vat_subject">
									<div class="vat-regime-wrapper">
										<BaseMultiselect
											id="vat-regime-select"
											data-name="document-vat-regime-select"
											:value="selectedVatRegimeOption ?? null"
											:options="vatRegimeOptions"
											:show-labels="false"
											:allow-empty="true"
											:searchable="false"
											:placeholder="$t('Régime de TVA')"
											label="text"
											track-by="value"
											custom-class="floating-model-menu"
											@input="selectedVatRegimeOption = $event"
										/>
									</div>
								</b-col>
								<b-col cols="5">
									<label class="sinao-label" for="writtingDate"> {{ $t('Date d’écriture') }}</label>
								</b-col>
								<b-col cols="7">
									<b-form-datepicker
										v-if="
											!documentId ||
												(documentId && loadedDocument && loadedDocument.status === 'draft')
										"
										id="writtingDate"
										v-model="writtingDate"
										:min="minWrittingDate"
										size="sm"
										:placeholder="$t('Date d’écriture')"
										class="
											billing__input
											billing__input--borderBottom
											billing__input--dashed
											billing__input--showAsWritable
											mb-2
										"
										style="padding-left: 7px"
										today-button
										:label-today-button="$t('Aujourd’hui')"
										close-button
										:label-close-button="$t('Fermer')"
										:locale="language"
										start-weekday="1"
										:hide-header="true"
										:date-format-options="{
											year: 'numeric',
											month: '2-digit',
											day: '2-digit'
										}"
									/>
									<label
										v-else
										class="
											form-control
											text-break text-wrap
											bg-transparent
											text-muted
											form-control-sm
										"
										style="
											color: var(--black) !important;
											border: none;
											margin-top: 4px;
											margin-left: -3px;
										"
									>
										{{ writtingDate }}
									</label>
								</b-col>
								<b-col cols="5">
									<label class="sinao-label" for="number">
										N°
										{{ $t(documentType) }}
									</label>
								</b-col>
								<b-col cols="7" v-b-tooltip.hover.top :title="tooltipBillingNumberMsg">
									<b-form-input
										v-if="
											!documentId ||
												(documentId && loadedDocument && loadedDocument.status === 'draft')
										"
										id="number"
										:value="billingNumber"
										class="
										billing__input
										billing__input--borderBottom
										billing__input--dashed
										billing__input--showAsWritable
										billing__input--small
										"
										size="sm"
										:placeholder="'N° ' + $t(documentType)"
										readonly
										style="cursor: pointer; background-color: var(--white)"
										@click="showBillingPopover = !showBillingPopover"
									/>
									<label
										v-else
										class="
											form-control
											text-break text-wrap
											bg-transparent
											text-muted
											form-control-sm
										"
										style="color: var(--black) !important; border: none; margin-top: 4px; margin-left: -3px"
									>
										{{ billingNumber }}
									</label>

									<PopoverBillingFormatRadioList
										v-model.lazy="billingNumber"
										:next-document="nextDocument"
										:popover-show.sync="showBillingPopover"
									/>
								</b-col>

							</b-row>
							<div v-if="page.type === 'quotes'">
								<b-col cols="5">
									<label class="sinao-label" for="commercialvalidityDeadline">
										{{ $t("Date d’expiration") }}
									</label>
								</b-col>
								<b-col cols="7">
									<b-form-datepicker
										id="deliveredAt"
										v-model="commercialvalidityDeadline"
										:min="minCommercialValidityDeadline"
										size="sm"
										:placeholder="$t('Date d’expiration')"
										class="
											billing__input
											billing__input--borderBottom
											billing__input--dashed
											billing__input--showAsWritable
											mb-2
										"
										style="padding-left: 7px"
										today-button
										:label-today-button="$t('Aujourd’hui')"
										close-button
										:label-close-button="$t('Fermer')"
										:locale="language"
										start-weekday="1"
										:hide-header="true"
										:date-format-options="{
											year: 'numeric',
											month: '2-digit',
											day: '2-digit'
										}"
									/>
								</b-col>
							</div>
							<div v-if="!showDetails" style="margin: 8px 0 0 -3px">
								<b-link @click="showDetails = !showDetails">
									<b-icon icon="plus" class="font-size-icon" aria-hidden="true" />
									{{ $t("Champs personnalisés") }}
								</b-link>
							</div>
							<div v-else>
								<b-row v-if="page.type === 'invoices'">
									<b-col cols="5">
										<label class="sinao-label" for="deliveredAt">
											{{ $t("Date de livraison") }}
										</label>
									</b-col>
									<b-col cols="7">
										<b-form-datepicker
											id="deliveredAt"
											v-model="deliveredAt"
											size="sm"
											:placeholder="$t('Date de livraison')"
											class="
												billing__input
												billing__input--borderBottom
												billing__input--dashed
												billing__input--showAsWritable
												mb-2
											"
											style="padding-left: 7px"
											today-button
											:label-today-button="$t('Aujourd’hui')"
											close-button
											:label-close-button="$t('Fermer')"
											:locale="language"
											start-weekday="1"
											:hide-header="true"
											:date-format-options="{
												year: 'numeric',
												month: '2-digit',
												day: '2-digit'
											}"
										/>
									</b-col>
								</b-row>
								<b-row>
									<b-col cols="5">
										<label class="sinao-label" for="commitment_number"> {{ $t("N° commande") }} </label>
									</b-col>
									<b-col cols="7" v-b-tooltip.hover.top :title="$t('Bon de commande ou numéro d’engagement.')">
										<b-form-input
											id="commitment_number"
											v-model.lazy="commitment_number"
											class="
												billing__input
												billing__input--borderBottom
												billing__input--dashed
												billing__input--showAsWritable
												billing__input--small
											"
											size="sm"
											style="cursor: pointer; background-color: var(--white)"
											:state="commitment_number_valid"
										/>
									</b-col>
								</b-row>
								<b-row>
									<b-col id="legaltext">
										<BaseStylableTextarea
											v-model.lazy="details"
											class="
												billing__input
												billing__input--borderBottom
												billing__input--dashed
												billing__input--auto-size
												billing__input--showAsWritable
											"
											:placeholder="$t('Autres détails')"
										/>
									</b-col>
								</b-row>
							</div>
						</Fragment>
						<b-row v-else>
							<b-col cols="12">
								{{ $t("Détails :") }}
							</b-col>

							<b-col cols="12">
								<b-overlay
									:show="home.savingDocument"
									rounded
									opacity="0.6"
									spinner-small
									spinner-variant="primary"
								>
									<BaseStylableTextarea
										v-model.lazy="details"
										class="
											billing__input
											billing__input--borderBottom
											billing__input--dashed
											billing__input--auto-size
											billing__input--showAsWritable
										"
										:placeholder="$t('Période de facturation et texte libre')"
									/>
								</b-overlay>
							</b-col>
							<!-- periods of recurrency -->
							<b-col
								v-show="recurring_frequencyDuration !== 'day'"
								cols="10 mt-2 mb-2"
							>
								{{ $t("Période de facturation") + " :" }}
							</b-col>
							<b-col
								v-show="recurring_frequencyDuration !== 'day'"
								cols="2 text-center"
							>
								<p
									id="info-recurring-period"
									class="h5 mt-2 mb-2"
									style="color: #999"
								>
									<b-icon-question-circle-fill />
								</p>
								<b-tooltip
									target="info-recurring-period"
									triggers="hover"
								>
									{{ $t("tooltip_reccurring_invoice_period") }}
								</b-tooltip>
							</b-col>
							<b-col
								v-show="recurring_frequencyDuration !== 'day'"
								cols="12"
							>
								<b-overlay
									:show="home.savingDocument"
									rounded
									opacity="0.6"
									spinner-small
									spinner-variant="primary"
								>
									<b-form-select
										v-model="recurring_period"
										:options="recurring_periods"
									/>
								</b-overlay>
							</b-col>

							<template v-if="isRecurringInvoice && isFrenchSalesDocument">
								<b-col cols="12" class="mt-2 mb-2">
									{{ $t("Type d’opération") + ' :' }}
								</b-col>
								<b-col cols="12" v-b-tooltip.hover.html.right="$t('tooltip_document_type')">
									<b-form-select
										id="vat-basis-select"
										data-name="recurring-document-type-select"
										v-model="vatType"
										:options="typeOptions"
										class="custom-select"
									/>
								</b-col>
							</template>
							<template v-if="isRecurringInvoice && isFrenchSalesDocument && is_vat_subject">
								<b-col cols="12" class="mt-2 mb-2" v-b-tooltip.hover.html.right="$t('tooltip_vat_regime')">
									{{ $t("TVA applicable") + ' :' }}
								</b-col>
								<b-col cols="12">
									<b-form-select
										id="vat-regime-select"
										data-name="recurring-document-vat-regime-select"
										v-model="vat_regime"
										:options="vatRegimeOptions"
										class="custom-select"
									/>
								</b-col>
							</template>
						</b-row>
					</b-col>

					<!-- CLIENT SELECTION -->
					<b-col cols="6">
						<b-row class="mx-0 pt-3">
							<b-card no-body class="sinao-card w-100">
								<ClientPicker :is-third-account="false" />
							</b-card>
						</b-row>
					</b-col>

					<!-- CLIENT SELECTION -->
					<b-col v-if="third_account_enabled == '1'" cols="6">
						<b-row class="mx-0 pt-3">
							<b-card no-body class="sinao-card w-100">
                <ClientPicker :is-third-account="true" />
							</b-card>
						</b-row>
					</b-col>
				</b-row>

				<!-- INVOICE TITLE & BILLING TABLE -->
				<b-row class="mt-3">
					<!-- INVOICE TITLE -->
					<b-col cols="12" class="mb-1 px-0">
						<BaseStylableTextarea
							v-model.lazy="title"
							class="billing__input billing__input--solid"
							:placeholder="page.type === 'invoices' ? $t('Titre de la facture') : $t('Titre du devis')"
						/>
					</b-col>
					<!-- BILLING TABLE -->
					<b-col cols="12" class="col">
						<BillingTable ref="billingTable" />
					</b-col>
				</b-row>

				<!-- DOWNPAYMENT TABLE -->
				<b-row>
					<DownpaymentTable
						v-if="page.type === 'invoices' && showDownpaymentTable === true"
						@event-removeDownpaymentTable="showDownpaymentTable = !showDownpaymentTable"
					/>
				</b-row>

				<!-- RIB SELECTION & COUPON CODE / VAT / TOTAL -->
				<b-row class="billing__row-sm-reverse">
					<b-col cols="6">
						<!-- Downpayment link -->
						<b-row v-if="appId !== 0 && !isRecurringInvoice" class="show-downpayment_legaltext font-size-small ml-1 pt-1">
							<b-link
								v-if="page.type === 'invoices' && showDownpaymentTable === false && isEditable"
								@click="showDownpaymentTable = !showDownpaymentTable"
							>
								<b-icon icon="plus" class="font-size-icon" aria-hidden="true" /> {{ $t("Factures d’acomptes") }}
							</b-link>
						</b-row>

						<!-- RIB SELECTION -->
						<b-row id="bank-details" class="mx-0 pt-3">
							<b-card no-body class="mr-3 sinao-card w-100">
								<BaseSelect
									v-model="selectedRIB"
									operation-id="app.documents.sales.bankdetails.update"
									:data="bankdetails"
									:placeholder="$t('Sélectionner un RIB')"
									text-field="name"
									class="base_select"
									:fields-to-map="[
										{
											key: 'name',
											rename: $t('Libellé')
										},
										{
											key: 'iban',
											rename: $t('IBAN')
										},
										{
											key: 'bic',
											rename: $t('BIC/SWIFT')
										}
									]"
								>
									<template #button>
										<div
											v-if="!selectedRIB"
											class="divTable"
										>
											<div
												class="divCell"
												style="width: 50%"
											>
												<b-button
													size="sm"
													class="sinao-button-default"
													block
													variant="light"
													@click="showBankModalDelete = true"
												>
													- {{ $t("Supprimer un RIB") }}
												</b-button>
											</div>
											<div
												class="divCell"
												style="width: 50%"
											>
												<b-button
													size="sm"
													class="sinao-button-default"
													block
													variant="light"
													@click="showBankModalCreate = true"
												>
													+ {{ $t("Créer un RIB") }}
												</b-button>
											</div>
										</div>
									</template>
								</BaseSelect>
							</b-card>
						</b-row>
					</b-col>

					<!-- COUPON CODE / VAT / TOTAL -->
					<b-col cols="6" class="table-vat">
						<VatTable />
					</b-col>
				</b-row>

				<b-row class="sinao-mention-row sinao-mention-spacer text-center">
					<b-col>
						<b-link
							v-if="page.type === 'invoices' && showDownpaymentWithoutInvoice === false"
							class="display-block show-downpayment_legaltext text-center"
							@click="showDownpaymentWithoutInvoice = true"
						>
							<b-icon icon="plus" class="font-size-icon" aria-hidden="true" />
							{{ $t("Acompte sans facture") }}
						</b-link>

						<DownpaymentWithoutInvoice v-else />
					</b-col>
				</b-row>

				<!-- MENTION 1 -->
				<b-row v-if="page.type === 'invoices'" class="mt-4 sinao-mention-row text-center">
					<b-col
						id="payment"
						class="stylable-textarea"
					>
						<DisabledStylableTextarea
							:value="paymentTextDays"
							:pointer="true"
							:alignCenter="true"
							v-b-tooltip.hover.right="$t('Méthode et délai de règlement')"
							class="billing__input billing__input--dashed billing__input--rounded"
							@click="onboarding.showPopoverPayment = true"
						/>
						<div
							id="paymentPoper"
							style="width:50px; height: 5px; position: relative; float:right; background-color: var(--white)"
						/>
					</b-col>
				</b-row>

				<!-- MENTION 2 -->
				<b-row v-if="vat.selectedVatValue === 0" class="mt-4">
					<b-form-select v-model="vatExonerationReason" size="sm" @change="selectVatExoneration" id="exonerationSelect" class="mb-2">
						<b-select-option v-for="item in vatExoneration" :key="String(item.reason)" :value="String(item.reason)">
							{{ $t(item.display) }}
						</b-select-option>
					</b-form-select>

					<BaseStylableTextarea
						v-model="vatExonerationArticle"
            			:nullable="true"
						class="billing__input billing__input--solid w-100"
					/>

					<b-tooltip
						placement="right"
						target="exonerationSelect"
						container="body"
					>
						{{ $t("L’ajout de nouvelles mentions est possible dans Paramètres généraux") }}
					</b-tooltip>
				</b-row>

				<!-- MENTION 3 -->
				<b-row class="mt-4 sinao-mention-row justify-content-end">
					<b-col cols="12" class="px-0">
						<BaseStylableTextarea
							v-model.lazy="mention"
							v-b-tooltip.hover.right="$t('Mentions légales : texte libre')"
							class="billing__input billing__input--dashed billing__input--rounded"
							:placeholder="$t('Mentions légales : texte libre')"
						/>
					</b-col>

					<b-col cols="2" class="text-right px-0">
						<b-form-checkbox
							v-model="mentionAsDefault"
							v-b-tooltip.hover.right="$t('Définir cette mention comme texte par défaut pour tous les prochains documents')"
							class="text-small mt-1"
							style="display: flex; justify-content: right; align-items: center"
							size="sm"
						>
							{{ $t("Par défaut") }}
						</b-form-checkbox>
					</b-col>

					<div
						v-if="showInvoicePenalities"
						id="penalityText"
						class="col-12 stylable-textarea hand"
						@click.stop="showPenalPopover = !showPenalPopover; showFooterPopover=false"
					>
						<b-col v-if="page.type === 'invoices'" cols="12" class="px-0">
							<p>
								<label
									for="penalityPercentage"
									class="d-inline hand"
								>
									{{ invoicesPenalities[0] }}
									<span class="sr-only">{{ $t("en poucentage") }}</span>
								</label>
								<input
									v-if="invoicesPenalities[1].length > 0"
									id="penalityPercentage"
									v-model="penalityPercentage"
									type="number"
									step=".01"
									class="
										billing__input
										billing__input--borderBottom
										billing__input--dashed
										billing__input--auto-size
										"
									min="0"
									:style="{
										width: calculateTextWidth(penalityPercentage) + 'px',
										'min-width': '2ch'
									}"
									@click.stop="closeAllPopover"
								>{{ invoicesPenalities[1] }}{{ invoicesPenalities[0].length > 0 ? ". " : "" }}
								<label for="penalityAmount" class="d-inline">
									{{ invoicesIndemnities[0] }}
									<span class="sr-only">{{ $t("en {currency}" , { currency: currency }) }}</span>
								</label>
								<input
									v-if="invoicesIndemnities[1].length > 0"
									v-model="penalityAmount"
									type="number"
									step=".01"
									class="
										billing__input
										billing__input--borderBottom
										billing__input--dashed
										billing__input--auto-size
										"
									min="0"
									:style="{
										width: calculateTextWidth(penalityAmount) + 'px',
										'min-width': '2ch',
										'max-width': '100%'
									}"
									@click.stop="closeAllPopover"
								>
								{{ invoicesIndemnities[1] }}
							</p>
						</b-col>
						<b-tooltip
							placement="right"
							target="penalityText"
							container="body"
						>
							{{ $t("% de pénalités") }}
							<br />
							{{ $t("{currency} d’indemnité", { currency: currency }) }}
						</b-tooltip>
						<PopoverPenal
							:popover-show.sync="showPenalPopover"
							:pagetype="page.type"
						/>
					</div>

					<b-col id="company-infos" cols="12" class="px-0 stylable-textarea text-center hand">
						<div @click.stop="showFooterPopover = !showFooterPopover; showPenalPopover= false">
							<div
								v-if="organization"
								v-html="legalMentionText"
							/>
						</div>
					</b-col>
					<b-tooltip
						placement="right"
						target="company-infos"
						container="body"
					>
						{{ $t("Profil de l’entreprise") }}
					</b-tooltip>
					<PopoverOrganizationProfile :popover-show.sync="showFooterPopover" />
				</b-row>

				<!-- FactureX & SUBMIT BUTTON -->
				<b-row
					class="mt-5 footer-btn relative"
				>
					<b-col
						cols="2"
						class="text-left footer-col"
						style="padding:0"
					>
						<b-img
							v-if="page.type === 'invoices'"
							id="factureX"
							:src="factureXLogo"
							fluid
							alt="Facture-X"
							style="max-width: 100px;"
						/>
						<b-tooltip
							placement="right"
							target="factureX"
							container="body"
						>
							{{ $t("100% compatible :") }} <br>
							{{ $t("Chorus Pro et facturation électronique") }}
						</b-tooltip>
					</b-col>
					<b-col
						cols="10"
						class="text-center footer-col m-auto"
						style="padding-left:0; padding-right:16.6%"
					>
						<b-overlay
							:show="home.savingDocument"
							rounded
							opacity="0.6"
							spinner-small
							spinner-variant="dark"
							class="d-inline-block"
						>
							<s-button
								type="success"
								:disabled="home.savingDocument"
								@click="saveDocument"
							>
								{{ page.type === "invoices" ? $t("Enregistrer la facture") : $t("Enregistrer le devis") }}
							</s-button>
						</b-overlay>
					</b-col>
				</b-row>

				<Menu />

				<!-- Editor -->
				<ModalBillingSave :status.sync="home.showSaveBillingModal" />
				<ModalBankAccountCreate :status.sync="showBankModalCreate" />
				<ModalBankAccountDelete :status.sync="showBankModalDelete" />

				<!-- Marketing -->
				<ModalRegister :status.sync="home.showRegisterModal" />

        <!-- contacts modals  -->
        <ModalCreateOrganization
            :status.sync="showCreateOrganizationModal"
            @close="closeModal('showCreateOrganizationModal')"
        />
        <ModalEditAddress
            :status.sync="showEditAddressModal"
            @close="closeModal('showEditAddressModal')"
        />
        <ModalCreatePerson
            :status.sync="showCreatePersonModal"
            @close="closeModal('showCreatePersonModal')"
        />
        <ModalEditEstablishmentContact
            :status.sync="showEditEstablishmentContactModal"
            @close="closeModal('showEditEstablishmentContactModal')"
        />

				<!-- Onboarding -->
				<Fragment v-if="onboarding.onboarding || showModalDocumentStyleCloseButton">
					<ModalDocumentStyle :status.sync="onboarding.showModalDocumentStyle" v-if="onboarding.popovers.showModalDocumentStyle || showModalDocumentStyleCloseButton" />
					<template v-if="!onboarding.showModalDocumentStyle">
						<PopoverLogo :status.sync="onboarding.showPopoverLogo" target-id="data-dz-thumbnail" v-if="onboarding.popovers.showPopoverLogo" />
						<PopoverNumbering :status.sync="onboarding.showPopoverNumbering" target-id="number" v-if="onboarding.popovers.showPopoverNumbering" />
						<PopoverCompany :status.sync="onboarding.showPopoverCompany" target-id="company-infos" v-if="onboarding.popovers.showPopoverCompany" />
						<PopoverBankDetails :status.sync="onboarding.showPopoverBankDetails" target-id="bank-details" v-if="onboarding.popovers.showPopoverBankDetails" />
						<PopoverEndOnboarding :status.sync="onboarding.showPopoverEndOnboarding" target-id="end-onboarding" v-if="onboarding.popovers.showPopoverEndOnboarding" />
					</template>
				</Fragment>
				<PopoverPayment
            :status.sync="onboarding.showPopoverPayment"
            target-id="paymentPoper"
            placement="left"
            container="body"
        />
			</b-col>
		</b-card>
	</div>
</template>

<style lang="scss">
	@import "../scss/variables.scss";
	@import "../scss/home.scss";
	@import "../scss/multiselect.scss";

	#penalityText {
		margin: 5px 0;
	}
	.sinao-label{
		white-space: nowrap;
		min-width: 64px;
	}
	.hand {
		cursor: pointer;
	}
	.line-number {
		width: 100%;
		line-height: 15px;
		text-align: left;
		padding-left: 5px;
		padding-top: 11px
	}
	#end-onboarding {
		position: relative;
		z-index:1;
	}
	.b-overlay-wrap.position-relative > .b-overlay.position-absolute {
		margin-left: 0!important;
		margin-right: 0!important;
	}
	.vat-basis-wrapper {
		display: flex;
		gap: 70px;
		margin-right: 10px;
	}
	.vat-basis-wrapper .multiselect__tags {
		border: 1px solid rgb(221, 221, 221);
	}

	.custom-select {
		min-width: 100px;
	}

	@media screen and (max-width: 768px) {
		.editHeader {
			flex-direction: column;

			.pr-4 {
				padding-right: 0 !important;
			}

			.col-6 {
				flex: 0 0 100%;
				max-width: 100%;

				.pt-3 {
					padding-top: 0 !important;
				}

				&:last-child {
					margin-top: 1.5rem !important;
				}
			}
		}

		.table-vat {
			margin-left: auto;
			flex: 0 0 100%;
			max-width: 100%;

			.custom-select {
				background-position-x: 0 !important;
			}
		}

		.billing__row-sm-reverse {
			.col-6:first-child {
				flex: 0 0 100%;
				max-width: 100%;
			}
		}

		.bank-details > .sinao-card {
			margin-right: 0 !important;
		}

		.sinao-mention-spacer {
			padding-top: 12px !important;
		}

		.show-downpayment_legaltext {
			width: 100%;
			justify-content: center;
		}

		.col-2 {
			flex: 0 0 30%;
			max-width: 30%;
		}

		#penalityText {
			margin: 0;
			padding: 0;
		}

		deliveredAt__dialog_ {
			transform: translate(-40%, 27px) !important;
		}

		#bank-details {
			.sinao-card {
				margin-right: 0 !important;
			}

			.divTable {
				display: flex !important;
				flex-direction: column-reverse;

				.divCell {
					width: 100% !important;
				}
			}
		}

		.footer-btn {
			display: flex !important;
			flex-direction: column-reverse;
			align-items: center;

			.footer-col {
				flex: 0 0 100%;
				max-width: 100%;

				margin-left: 0 !important;
				padding-right: 0 !important;
				text-align: center !important;

				&:last-child {
					margin-bottom: 1rem !important;
				}
			}
		}
	}
</style>
